import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { EnvironmentProvider } from './context/EnvironmentContext';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import './services/monitoringService';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProtectedRoute from './components/ProtectedRoute';
import './styles/globals.css';

//  Sayfalar
import Login from './pages/auth/Login';
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Collections = lazy(() => import('./pages/collections/Collections'));
const CollectionDetail = lazy(() => import('./pages/collections/CollectionDetail'));
const CollectionRunner = lazy(() => import('./pages/collections/CollectionRunner'));
const EndpointList = lazy(() => import('./pages/endpoints/EndpointList'));
const RequestHistory = lazy(() => import('./pages/history/RequestHistory'));
const EnvironmentPage = lazy(() => import('./pages/environment/EnvironmentPage'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const ApiTesterPage = lazy(() => import('./pages/api-tester/ApiTesterPage'));

//  History artık ayrı component olarak yükleniyor

const Help = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-primary mb-4">Yardım</h1>
    <p className="text-secondary">Destek için lütfen sistem yöneticinize başvurun.</p>
  </div>
);

const MonitoringDashboard = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-primary mb-4">İzleme Paneli</h1>
    <p className="text-secondary mb-4">Sistem izleme aktif.</p>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="bg-secondary rounded-lg p-4">
        <h3 className="text-sm font-medium text-secondary mb-1">Durum</h3>
        <p className="text-2xl font-bold text-green-500">Aktif</p>
      </div>
      <div className="bg-secondary rounded-lg p-4">
        <h3 className="text-sm font-medium text-secondary mb-1">Sayfa Görüntüleme</h3>
        <p className="text-2xl font-bold text-blue-500">{window.monitoringService?.getSummaryMetrics()?.totalPageViews || 0}</p>
      </div>
      <div className="bg-secondary rounded-lg p-4">
        <h3 className="text-sm font-medium text-secondary mb-1">Hatalar</h3>
        <p className="text-2xl font-bold text-red-500">{window.monitoringService?.getSummaryMetrics()?.totalErrors || 0}</p>
      </div>
      <div className="bg-secondary rounded-lg p-4">
        <h3 className="text-sm font-medium text-secondary mb-1">Çalışma Süresi</h3>
        <p className="text-2xl font-bold text-purple-500">{window.monitoringService?.getSummaryMetrics()?.uptime || 0}s</p>
      </div>
    </div>
  </div>
);

const MetricsEndpoint = () => {
  const [metrics, setMetrics] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/metrics`);
      const text = await res.text();
      setMetrics(text);
    } catch (e) {
      setMetrics('Metrikler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMetrics(); }, []);

  return (
    <div className="p-6 bg-primary min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-primary mb-6">TEGIM Metrikleri</h1>
        <button onClick={fetchMetrics} className="mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
          {loading ? 'Yükleniyor...' : 'Metrikleri Yenile'}
        </button>
        <div className="bg-secondary rounded-lg p-6">
          <h2 className="text-lg font-semibold text-primary mb-4">Prometheus Metrikleri</h2>
          <pre className="bg-gray-900 text-green-400 p-4 rounded text-sm overflow-x-auto whitespace-pre">
            {metrics || 'Metrikler yükleniyor...'}
          </pre>
        </div>
      </div>
    </div>
  );
};

const useSimpleMonitoring = () => {
  const trackUserInteraction = (action, component) => {
    if (window.monitoringService) {
      window.monitoringService.trackUserInteraction(action, component);
    }
  };
  return { trackUserInteraction };
};

const useAppInitialization = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  return { isInitialized };
};

const AppLoading = () => (
  <div className="min-h-screen bg-primary flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <h2 className="text-lg font-medium text-primary mb-2">Tegim Yükleniyor...</h2>
      <p className="text-sm text-secondary">Uygulama hazırlanıyor</p>
    </div>
  </div>
);

function AppContent() {
  const { trackUserInteraction } = useSimpleMonitoring();

  useEffect(() => {
    trackUserInteraction('app_start', 'application');
  }, [trackUserInteraction]);

  return (
    <div className="App bg-primary min-h-screen transition-all duration-300">
      <Routes>
        <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
        <Route path="/monitoring" element={<MonitoringDashboard />} />
        <Route path="/api/metrics" element={<MetricsEndpoint />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Layout>
                <Suspense fallback={<AppLoading /> }>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                    <Route path="/collections" element={<ErrorBoundary><Collections /></ErrorBoundary>} />
                    <Route path="/collections/:id" element={<ErrorBoundary><CollectionDetail /></ErrorBoundary>} />
                    <Route path="/collections/:id/run" element={<ErrorBoundary><CollectionRunner /></ErrorBoundary>} />
                    <Route path="/endpoints" element={<ErrorBoundary><EndpointList /></ErrorBoundary>} />
                    <Route path="/environment" element={<ErrorBoundary><EnvironmentPage /></ErrorBoundary>} />
                    <Route path="/history" element={<ErrorBoundary><RequestHistory /></ErrorBoundary>} />
                    <Route path="/api-tester" element={<ErrorBoundary><ApiTesterPage /></ErrorBoundary>} />
                    <Route path="/settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />
                    <Route path="/help" element={<ErrorBoundary><Help /></ErrorBoundary>} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Suspense>
              </Layout>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
      </Routes>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar theme="colored" />
    </div>
  );
}

function App() {
  const { isInitialized } = useAppInitialization();

  if (!isInitialized) {
    return <AppLoading />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <EnvironmentProvider>
              <AppContent />
            </EnvironmentProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
